import type {
  AnalysisOutput,
  AnalyzeOptions,
  AuthResponse,
  CadenceResponse,
  LoginRequest,
  LoginResponse,
  MarkPublishedRequest,
  PasswordResetConfirm,
  PasswordResetRequest,
  ProviderConfig,
  ProviderId,
  PublicUser,
  RegisterRequest,
  SetWeeklyGoalRequest,
  TotpEnableRequest,
  TotpSetupResponse,
  TotpStatus,
  TotpVerifyRequest,
  UploadPreview,
  VerifyEmailRequest,
} from '@smartapply/shared';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3030';

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class SmartApplyApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

let csrfTokenCache: string | null = null;

async function ensureCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  const res = await fetch(`${API_URL}/api/v1/csrf-token`, {
    credentials: 'include',
  });
  const data = (await res.json()) as { csrfToken: string };
  csrfTokenCache = data.csrfToken;
  return data.csrfToken;
}

export function clearCsrfCache(): void {
  csrfTokenCache = null;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  if (res.ok) return (await res.json()) as T;

  let errorPayload: { error?: ApiError } = {};
  try {
    errorPayload = (await res.json()) as { error?: ApiError };
  } catch {
    // body sem JSON
  }

  const code = errorPayload.error?.code ?? `http_${res.status}`;
  const message = errorPayload.error?.message ?? `HTTP ${res.status}`;
  throw new SmartApplyApiError(code, message, res.status, errorPayload.error?.details);
}

interface PostJsonOptions {
  csrf?: boolean;
}

async function postJson<TRes>(
  path: string,
  body: unknown,
  opts: PostJsonOptions = { csrf: true },
): Promise<TRes> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.csrf !== false) {
    headers['X-CSRF-Token'] = await ensureCsrfToken();
  }
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  });
  return handleResponse<TRes>(res);
}

// ============================================================
// Auth (Fase 6)
// ============================================================

export async function authRegister(payload: RegisterRequest): Promise<AuthResponse> {
  return postJson<AuthResponse>('/api/v1/auth/register', payload);
}

export async function authLogin(payload: LoginRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>('/api/v1/auth/login', payload);
}

export async function totpVerify(payload: TotpVerifyRequest): Promise<AuthResponse> {
  return postJson<AuthResponse>('/api/v1/auth/totp/verify', payload);
}

export async function totpStatus(): Promise<TotpStatus> {
  const res = await fetch(`${API_URL}/api/v1/auth/totp/status`, { credentials: 'include' });
  return handleResponse<TotpStatus>(res);
}

export async function totpSetup(): Promise<TotpSetupResponse> {
  return postJson<TotpSetupResponse>('/api/v1/auth/totp/setup', {});
}

export async function totpEnable(payload: TotpEnableRequest): Promise<{ ok: true }> {
  return postJson<{ ok: true }>('/api/v1/auth/totp/enable', payload);
}

export async function totpDisable(password: string): Promise<{ ok: true }> {
  return postJson<{ ok: true }>('/api/v1/auth/totp/disable', { password });
}

export async function totpRegenerateRecovery(
  password: string,
): Promise<{ recoveryCodes: string[] }> {
  return postJson<{ recoveryCodes: string[] }>(
    '/api/v1/auth/totp/regenerate-recovery',
    { password },
  );
}

// ============================================================
// Cadência
// ============================================================

export async function fetchCadence(): Promise<CadenceResponse> {
  const res = await fetch(`${API_URL}/api/v1/cadence`, { credentials: 'include' });
  return handleResponse<CadenceResponse>(res);
}

export async function setWeeklyGoal(
  payload: SetWeeklyGoalRequest,
): Promise<{ weeklyGoal: number | null }> {
  return postJson<{ weeklyGoal: number | null }>('/api/v1/cadence/goal', payload);
}

export async function markDraftPublished(payload: MarkPublishedRequest): Promise<{
  id: string;
  publishedAt: string | null;
  publishedDraftId: string | null;
  publishedDrafts: { draftId: string | null; publishedAt: string }[];
}> {
  return postJson('/api/v1/cadence/published', payload);
}

export async function unmarkDraftPublished(payload: {
  draftSetId: string;
  draftId: string | null;
  publishedAt: string;
}): Promise<{
  id: string;
  publishedAt: string | null;
  publishedDraftId: string | null;
  publishedDrafts: { draftId: string | null; publishedAt: string }[];
}> {
  return postJson('/api/v1/cadence/published/unmark', payload);
}

export async function authLogout(): Promise<void> {
  await postJson<void>('/api/v1/auth/logout', {});
  clearCsrfCache();
}

export async function authMe(): Promise<{ user: PublicUser } | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/me`, {
      credentials: 'include',
    });
    if (res.status === 401) return null;
    return await handleResponse<{ user: PublicUser }>(res);
  } catch (err) {
    if (err instanceof SmartApplyApiError && err.status === 401) return null;
    throw err;
  }
}

export async function authPasswordResetRequest(payload: PasswordResetRequest): Promise<void> {
  await postJson<void>('/api/v1/auth/password-reset/request', payload);
}

export async function authPasswordResetConfirm(
  payload: PasswordResetConfirm,
): Promise<AuthResponse> {
  return postJson<AuthResponse>('/api/v1/auth/password-reset/confirm', payload);
}

export async function authVerifyEmail(payload: VerifyEmailRequest): Promise<{ verified: boolean }> {
  return postJson<{ verified: boolean }>('/api/v1/auth/verify', payload);
}

// ============================================================
// Upload / Provider / Analyze
// ============================================================

export async function uploadPdf(file: File): Promise<UploadPreview> {
  const form = new FormData();
  form.append('file', file);

  const headers: Record<string, string> = {
    'X-CSRF-Token': await ensureCsrfToken(),
  };

  const res = await fetch(`${API_URL}/api/v1/upload`, {
    method: 'POST',
    body: form,
    credentials: 'include',
    headers,
  });
  return handleResponse<UploadPreview>(res);
}

export interface ProviderOption {
  id: ProviderId;
  models: ReadonlyArray<{ id: string; label: string; tier: string }>;
}

export async function fetchProviderOptions(): Promise<{ providers: ProviderOption[] }> {
  const res = await fetch(`${API_URL}/api/v1/provider/options`, {
    credentials: 'include',
  });
  return handleResponse(res);
}

export async function validateProvider(
  config: ProviderConfig,
): Promise<{ valid: true; provider: string; modelId: string }> {
  return postJson('/api/v1/provider/validate', config);
}

export async function fetchProviderCurrent(): Promise<
  | { configured: false }
  | { configured: true; provider: ProviderId; modelId: string | null }
> {
  const res = await fetch(`${API_URL}/api/v1/provider/current`, {
    credentials: 'include',
  });
  return handleResponse(res);
}

export async function runAnalyze(input: {
  snapshotId: string;
  options?: Partial<AnalyzeOptions>;
}): Promise<AnalysisOutput> {
  // Inclui profile cacheado como fallback. Server usa só se byokStore perdeu
  // o snapshot (restart/TTL); caso contrário ignora.
  const { getCachedProfile } = await import('./profile-session.js');
  const profile = getCachedProfile();
  return postJson<AnalysisOutput>('/api/v1/analyze', {
    snapshotId: input.snapshotId,
    options: input.options,
    profile: profile ?? undefined,
  });
}
